import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { ButtonModule, EmptyComponent, IconModule, MultiSelectModule, SelectModule } from '@hglobal/fin-ui';
import { TranslateModule } from '@ngx-translate/core';

import { environment } from '@kr/environment';
import { DictUser, WalletCard } from '@kr/models';

import { WALLET_USERS_API, WalletUsersApiService, WalletUsersMockApi } from './api';
import { WalletUsersStore } from './wallet-users.store';

@Component({
  imports: [TranslateModule, IconModule, ButtonModule, FormsModule, ReactiveFormsModule, SelectModule, MultiSelectModule, EmptyComponent],
  providers: [
    {
      provide: WALLET_USERS_API,
      useClass: environment.useMocks ? WalletUsersMockApi : WalletUsersApiService,
    },
    WalletUsersStore,
  ],
  selector: 'kr-wallet-users',
  templateUrl: './wallet-users.component.html',
  styleUrls: ['./wallet-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalletUsersComponent implements OnInit {
  readonly #store = inject(WalletUsersStore);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);
  #prevUserIds: number[] = [];

  wallet = input<WalletCard>();

  users = this.#store.users;
  userIds = this.#store.userIds;
  selectedUsers = this.#store.selectedUsers;

  form = this.#fb.group({
    users: [],
  });

  userIdsEffect = effect(() => {
    if (!this.userIds().length) return;

    this.#prevUserIds = this.userIds();
    this.form.patchValue({ users: this.userIds() });
  });

  ngOnInit(): void {
    this.#store.getUserIds(this.wallet());

    this.#subscribeToUsersControl();
  }

  remove(user: DictUser): void {
    const ids = this.#prevUserIds.filter(id => id !== user.id);
    this.form.patchValue({ users: ids });
  }

  #save(ids: number[]): void {
    const addedUsers = ids.filter(id => !this.#prevUserIds.includes(id));
    const removedUsers = this.#prevUserIds.filter(id => !ids.includes(id));

    if (addedUsers.length) {
      this.#store.addUsers({ wallet: this.wallet(), userIds: addedUsers });
    }

    if (removedUsers.length) {
      this.#store.removeUsers({ wallet: this.wallet(), userIds: removedUsers });
    }

    this.#prevUserIds = [...ids];
  }

  #subscribeToUsersControl(): void {
    this.form
      .get('users')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(ids => this.#save(ids));
  }
}
